import { useParams } from "react-router-dom";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  OrderDetailsProvider,
  useOrderDetails,
} from "../../context/OrderDetailContext";
import { getOrderDetails } from "../../services/orders/orderAxios";
import Loader from "../../components/common/Loader";
import { useAuth } from "../../context/AuthContext";
import { MastershopState } from "../SalesPage/SalesPageInterface";
import { handleApi } from "../../utils/handleApi";
import { ConfirmationInfo, getConfirmationStatuses } from "../../services/whatsapp/whatsappService";
import { decodeTokenService } from "../../services/decodeTokenService";
import { OrderTopBar } from "./components/OrderTopBar";
import { OrderProductsCard } from "./components/OrderProductsCard";
import { OrderIntegrationsCard } from "./components/OrderIntegrationsCard";
import { hasOrderIntegrations } from "./components/orderTotals";
import { OrderPropertiesPanel } from "./components/OrderPropertiesPanel";
import { OrderDeliveryCard } from "./components/OrderDeliveryCard";

const OrderDetailsContent = () => {
  const { language, timezone } = useAuth();
  const {
    products,
    customer,
    shippingAddress,
    orderDetails,
    loading,
    error,
    setOrderDetailsData,
    setLoadingState,
    setErrorState,
  } = useOrderDetails();

  const { hash_id } = useParams();
  const dataLoadedRef = useRef(false);
  const [errorOrderDetails, setErrorOrderDetails] = useState<string>();
  const [confirmationInfo, setConfirmationInfo] = useState<ConfirmationInfo | null>(null);

  const customFields = customer?.custom_fields ?? [];
  const normalizeKey = (k: unknown) => String(k ?? "").trim().toLowerCase();
  const isZipKey = (key: unknown) => ["zip", "zip code", "postal_code", "postal code", "codigo postal", "código postal"].includes(normalizeKey(key));
  const zipCodeField = customFields.find((f) => isZipKey(f?.key));
  const zipCode = zipCodeField?.value ? String(zipCodeField.value) : undefined;

  const fetchOrderDetails = useCallback(async () => {
    if (!hash_id) return;
    setLoadingState(true);
    setErrorOrderDetails(undefined);
    const { res, error } = await handleApi(getOrderDetails(hash_id), false);

    if (error) {
      setErrorState("Failed to load order details");
      setLoadingState(false);
      return;
    }

    if (res) {
      if (res.order?.dropi_integration_error) {
        setErrorOrderDetails(res.dropi_integration_error || res.order.dropi_integration_error);
      }
      setOrderDetailsData(res);
      setLoadingState(false);
      dataLoadedRef.current = true;
    }
  }, [hash_id, setErrorState, setLoadingState, setOrderDetailsData]);

  const refreshOrderDetails = useCallback(async () => {
    if (!hash_id) return;
    const { res, error } = await handleApi(getOrderDetails(hash_id), false);

    if (!error && res) {
      if (res.order?.dropi_integration_error) {
        setErrorOrderDetails(res.dropi_integration_error || res.order.dropi_integration_error);
      }
      setOrderDetailsData(res);
      dataLoadedRef.current = true;
    }
  }, [hash_id, setOrderDetailsData]);

  useEffect(() => {
    if (dataLoadedRef.current || !hash_id) {
      if (!hash_id) {
        setErrorState("No order ID provided");
      }
      return;
    }
    fetchOrderDetails();
  }, [hash_id, fetchOrderDetails, setErrorState]);

  useEffect(() => {
    if (!orderDetails?.id) return;
    const ownerId = decodeTokenService.getUserIdFromToken();
    if (!ownerId) return;
    const fetchConfirmation = async () => {
      const { res, error: err } = await handleApi(getConfirmationStatuses(ownerId, [orderDetails.id]), false);
      if (!err && res) {
        const info = res[String(orderDetails.id)];
        if (info) setConfirmationInfo(info);
      }
    };
    fetchConfirmation();
  }, [orderDetails?.id]);

  useEffect(() => {
    const isMastershopPending =
      orderDetails?.mastershop_state === MastershopState.PENDING_SYNC ||
      orderDetails?.mastershop_state === MastershopState.WAITING_PAYMENT;

    if (!isMastershopPending) return;

    let attempts = 0;
    const intervalId = window.setInterval(() => {
      attempts += 1;
      if (attempts > 8) {
        window.clearInterval(intervalId);
        return;
      }
      refreshOrderDetails();
    }, 15000);

    return () => window.clearInterval(intervalId);
  }, [orderDetails?.id, orderDetails?.mastershop_state, refreshOrderDetails]);

  const handleAddressUpdated = () => {
    dataLoadedRef.current = false;
    refreshOrderDetails();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader />
      </div>
    );
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  if (!orderDetails) return null;

  // Con integraciones, alturas naturales: expandir el detalle de una fila no
  // debe redimensionar la card de productos. Sin integraciones, productos
  // estira para emparejar con el panel (sin hueco).
  const withIntegrations = hasOrderIntegrations(orderDetails);

  return (
    <div className="min-h-screen">
      <OrderTopBar order={orderDetails} confirmationInfo={confirmationInfo} />

      <div
        className={`grid grid-cols-1 gap-4 px-4 pb-10 pt-1 md:px-16 lg:grid-cols-[minmax(0,1fr)_340px] ${
          withIntegrations ? "items-start" : "items-stretch"
        }`}
      >
        <div className="flex min-w-0 flex-col gap-4">
          <OrderProductsCard
            order={orderDetails}
            products={products}
            className={`animate-order-fade-up ${withIntegrations ? "" : "flex-1"}`}
          />
          <OrderDeliveryCard
            order={orderDetails}
            variant="inline"
            className="animate-order-fade-up"
            style={{ animationDelay: "80ms" }}
            onStatusChanged={refreshOrderDetails}
          />
          <OrderIntegrationsCard
            order={orderDetails}
            customer={customer}
            dropiError={errorOrderDetails}
            timezone={timezone}
            language={language}
            onShopifyRetryComplete={fetchOrderDetails}
            onMastershopRetryComplete={refreshOrderDetails}
            onDropiSynced={handleAddressUpdated}
            className="animate-order-fade-up"
            style={{ animationDelay: "140ms" }}
          />
        </div>

        <OrderPropertiesPanel
          className="animate-order-fade-up"
          style={{ animationDelay: "140ms" }}
          order={orderDetails}
          customer={customer}
          products={products}
          shippingAddress={shippingAddress}
          zipCode={zipCode}
          confirmationId={confirmationInfo?.confirmation_id}
          aiEnabled={confirmationInfo?.ai_enabled}
          onAddressUpdated={handleAddressUpdated}
        />
      </div>
    </div>
  );
};

export const OrderDetailsPage = () => {
  const { hash_id } = useParams();

  return (
    <OrderDetailsProvider hashId={hash_id}>
      <OrderDetailsContent />
    </OrderDetailsProvider>
  );
};
